<template>
  <component
    :is="as"
    :id="uniqueId"
    v-bind="attrs"
    tabindex="0"
    :class="[
      $style.filterSetItem,
      {
        [$style.selected]: selected,
        [$style.hovered]: hovered,
        [$style.mobile]: isMobile,
      },
    ]"
    @click="emit('click', $event)"
    @mouseover="hovered = true"
    @mouseleave="hovered = false"
  >
    <div :class="$style.content">
      <slot></slot>
    </div>

    <div
      v-show="editable && (hovered || isMobile)"
      :class="[
        $style.editIcon,
        {
          [$style.selected]: selected,
          [$style.hovered]: hovered,
          [$style.mobile]: isMobile,
        },
      ]"
      @click.stop="emit('edit')"
    >
      <SCASvgIcon icon="phosphor/pencil-simple" size="12px" />
    </div>
  </component>
  <SCATooltip v-if="tooltip" :id="uniqueId" :text="tooltip ?? ''" />
</template>

<script setup lang="ts">
import { ref, useAttrs, useId } from "vue";

import SCASvgIcon from "./SCASvgIcon.vue";
import SCATooltip from "./SCATooltip.vue";
import type { SCAFilterSetItemProps } from "./types";

withDefaults(defineProps<SCAFilterSetItemProps>(), {
  disabled: false,
  selected: false,
  editable: false,
  isMobile: false,
  tooltip: undefined,
  as: "div",
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();

const uniqueId = useId();
const hovered = ref(false);

const emit = defineEmits<{
  click: [event: MouseEvent];
  edit: [];
}>();
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.filterSetItem {
  display: inline-flex;
  align-items: center;
  position: relative;
  gap: var(--space-component-gap-xSmall);
  height: var(--size-2xSmall);
  min-height: var(--size-2xSmall);
  flex: 0 0 auto;

  @include FigmaTypoMixins.typo-body-medium;
  color: var(--color-content-primary);

  background-color: transparent;
  overflow: hidden;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  .content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .editIcon {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    height: var(--size-2xSmall);
    min-height: var(--size-2xSmall);
    padding: var(--space-component-padding-xSmall);
    border-radius: var(--border-radius-medium);
    box-shadow: -0.83px 0.83px 3.33px rgba(0, 0, 0, 0.35);
    cursor: pointer;
    position: absolute;
    right: 0;

    & svg {
      @include IconMixins.icon-color(var(--color-content-primary));
      pointer-events: none;
    }
  }

  &.mobile {
    padding: var(--space-component-padding-xSmall);
    height: auto;
    min-height: auto;
    border-radius: var(--border-radius-medium);

    .editIcon {
      position: static;
      box-shadow: none;
      padding: 0;
    }
  }
}

.hovered {
  color: var(--color-action-primary-active);

  &.mobile {
    color: unset;
    border-bottom: unset;
    background-color: var(--color-coolGrey-p3);
  }
}

.selected {
  color: var(--color-action-primary-active);
  border-bottom: 1px solid var(--color-action-primary-active);

  &.mobile {
    color: unset;
    border-bottom: unset;
    background-color: var(--color-coolGrey-p3);
  }
}
</style>
